import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import { useAgentSocket } from '../hooks/useAgentSocket';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './AgentPage.css';

const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Nå';
  if (diffMins < 60) return `${diffMins} min siden`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} time${diffHours > 1 ? 'r' : ''} siden`;
  return date.toLocaleDateString('nb-NO');
};

const getChatPreview = (chat, maxLength = 84) => {
  const text = chat?.lastMessage || 'Ingen melding ennå';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const TAB_LABELS = {
  chat: 'Samtale',
  my: 'Mine samtaler',
  queue: 'Ventekø',
};

const getOfferSecondsLeft = (offer) => {
  if (!offer) return 0;
  if (offer.expiresAt) {
    const seconds = Math.ceil((new Date(offer.expiresAt).getTime() - Date.now()) / 1000);
    if (Number.isFinite(seconds)) return Math.max(0, seconds);
  }
  return offer.timeoutSeconds || 30;
};

const AgentPage = () => {
  const { selectedCompany, permissions } = useAuth();
  const companyId = selectedCompany?.id ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    socketReady,
    isConnected,
    connectionState,
    on: socketOn,
    acceptChat: socketAcceptChat,
    declineChat: socketDeclineChat,
    sendMessage: socketSendMessage,
    sendTyping: socketSendTyping,
    handBackToAI: socketHandBackToAI,
    resolveChat: socketResolveChat,
    myChats,
    pendingQueue,
    incomingAgentOffer,
    agentQueuesLoading,
    agentDesiredAvailable,
    agentIsAvailable,
    refreshAgentQueues,
    activeAgentChatId,
    setActiveAgentChatId,
  } = useAgentSocket(companyId);

  const [activeTab, setActiveTab] = useState('chat');
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [offerTimeLeft, setOfferTimeLeft] = useState(0);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const selectedChatIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [todayResolved, setTodayResolved] = useState(0);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const urlChatId = searchParams.get('chat');
  const urlOfferId = searchParams.get('offer');
  const urlTab = searchParams.get('tab');

  const setAgentParams = useCallback((next = {}) => {
    const params = new URLSearchParams();
    if (next.chatId) {
      params.set('chat', next.chatId);
    } else if (next.tab && next.tab !== 'chat') {
      params.set('tab', next.tab);
    }
    setSearchParams(params);
  }, [setSearchParams]);

  const loadMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      const msgs = await DashboardApi.getAgentChatMessages(chatId, 200);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch (err) {
      console.error('Failed to load messages', err);
      notifyError(err.message || 'Kunne ikke hente samtalen.', { source: 'Agent' });
    }
  }, []);

  const selectChat = useCallback(async (chatId) => {
    if (!chatId) return;
    setSelectedChatId(chatId);
    setActiveAgentChatId(chatId);
    setActiveTab('chat');
    setAgentParams({ chatId });
    await loadMessages(chatId);
  }, [loadMessages, setActiveAgentChatId, setAgentParams]);

  const selectedAssignedChat = useMemo(() => (
    myChats.find((chat) => String(chat.chatId) === String(selectedChatId))
    || null
  ), [myChats, selectedChatId]);
  const selectedPendingChat = useMemo(() => (
    pendingQueue.find((chat) => String(chat.chatId) === String(selectedChatId))
    || null
  ), [pendingQueue, selectedChatId]);

  useEffect(() => {
    if (urlOfferId) {
      setActiveTab('chat');
      setSelectedChatId(null);
      setMessages([]);
      setActiveAgentChatId(null);
      if (incomingAgentOffer && String(incomingAgentOffer.chatId) === String(urlOfferId)) {
        setIncomingOffer(incomingAgentOffer);
        setOfferTimeLeft(getOfferSecondsLeft(incomingAgentOffer));
      }
      return;
    }

    if (urlChatId && urlChatId !== selectedChatIdRef.current) {
      setSelectedChatId(urlChatId);
      setActiveAgentChatId(urlChatId);
      setActiveTab('chat');
      return;
    }

    if (!urlChatId && activeAgentChatId && !selectedChatIdRef.current) {
      setSelectedChatId(activeAgentChatId);
      setActiveTab('chat');
      return;
    }

    if (!urlChatId && (urlTab === 'my' || urlTab === 'queue')) {
      setActiveTab(urlTab);
    }
  }, [activeAgentChatId, incomingAgentOffer, setActiveAgentChatId, urlChatId, urlOfferId, urlTab]);

  useEffect(() => {
    if (incomingAgentOffer) {
      setIncomingOffer(incomingAgentOffer);
      setOfferTimeLeft(getOfferSecondsLeft(incomingAgentOffer));
    }
  }, [incomingAgentOffer]);

  useEffect(() => {
    if (selectedAssignedChat?.chatId) {
      loadMessages(selectedAssignedChat.chatId);
    }
  }, [loadMessages, selectedAssignedChat?.chatId]);

  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
    setActiveAgentChatId(selectedChatId);
  }, [selectedChatId, setActiveAgentChatId]);

  useEffect(() => {
    if (!companyId || !socketReady) return undefined;

    const offChatOffer = socketOn('chat_offer', (data) => {
      setIncomingOffer(data);
      setOfferTimeLeft(getOfferSecondsLeft(data));
    });

    const offOfferExpired = socketOn('chat_offer_expired', (data) => {
      setIncomingOffer((current) => (
        current?.chatId === data.chatId ? null : current
      ));
    });

    const offNewMessage = socketOn('new_customer_message', (data) => {
      if (selectedChatIdRef.current === data.chatId) {
        loadMessages(data.chatId);
      }
    });

    const offChatClaimed = socketOn('chat_claimed', (data) => {
      setIncomingOffer((current) => (
        current?.chatId === data.chatId ? null : current
      ));
    });

    const offChatClosed = socketOn('chat_closed', (data) => {
      if (selectedChatIdRef.current === data.chatId) {
        setSelectedChatId(null);
        setMessages([]);
        setAgentParams({ tab: 'my' });
      }
      setTodayResolved((prev) => prev + 1);
    });

    const offChatHandedBack = socketOn('chat_handed_back', (data) => {
      if (selectedChatIdRef.current === data.chatId) {
        setSelectedChatId(null);
        setMessages([]);
        setAgentParams({ tab: 'my' });
      }
    });

    refreshAgentQueues();

    return () => {
      offChatOffer();
      offOfferExpired();
      offNewMessage();
      offChatClaimed();
      offChatClosed();
      offChatHandedBack();
    };
  }, [companyId, loadMessages, refreshAgentQueues, setAgentParams, socketOn, socketReady]);

  useEffect(() => {
    if (!incomingOffer || offerTimeLeft <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setOfferTimeLeft((timeLeft) => timeLeft - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [incomingOffer, offerTimeLeft]);

  useEffect(() => {
    if (incomingOffer && offerTimeLeft <= 0) {
      setIncomingOffer(null);
    }
  }, [incomingOffer, offerTimeLeft]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const acceptOffer = async () => {
    if (!incomingOffer) return;
    try {
      await socketAcceptChat(incomingOffer.chatId);
      setIncomingOffer(null);
      await refreshAgentQueues();
      await selectChat(incomingOffer.chatId);
    } catch (err) {
      console.error('Failed to accept offer', err);
      notifyError(err.message || 'Kunne ikke ta samtalen.', { source: 'Agent' });
    }
  };

  const declineOffer = async () => {
    if (!incomingOffer) return;
    try {
      await socketDeclineChat(incomingOffer.chatId);
      setIncomingOffer(null);
      await refreshAgentQueues();
    } catch (err) {
      console.error('Failed to decline offer', err);
      notifyError(err.message || 'Kunne ikke avvise samtalen.', { source: 'Agent' });
    }
  };

  const claimChat = async (chatId) => {
    try {
      await socketAcceptChat(chatId);
      await refreshAgentQueues();
      await selectChat(chatId);
    } catch (err) {
      console.error('Failed to claim chat', err);
      notifyError(err.message || 'Kunne ikke ta samtalen.', { source: 'Agent' });
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChatId || isSending) return;

    setIsSending(true);
    try {
      await socketSendMessage(selectedChatId, newMessage.trim());
      setNewMessage('');
      await loadMessages(selectedChatId);
      await refreshAgentQueues();
    } catch (err) {
      console.error('Failed to send message', err);
      notifyError(err.message || 'Kunne ikke sende meldingen.', { source: 'Agent' });
    } finally {
      setIsSending(false);
    }
  };

  const handleTyping = () => {
    if (!selectedChatId) return;
    socketSendTyping(selectedChatId, true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socketSendTyping(selectedChatId, false);
    }, 2000);
  };

  const handBackToAI = async () => {
    if (!selectedChatId) return;
    try {
      await socketHandBackToAI(selectedChatId);
      setSelectedChatId(null);
      setMessages([]);
      setActiveAgentChatId(null);
      setAgentParams({ tab: 'my' });
      await refreshAgentQueues();
      notifySuccess('Samtalen ble sendt tilbake til AI.', { source: 'Agent' });
    } catch (err) {
      console.error('Failed to hand back to AI', err);
      notifyError(err.message || 'Kunne ikke sende samtalen tilbake til AI.', { source: 'Agent' });
    }
  };

  const resolveChat = async () => {
    if (!selectedChatId) return;
    try {
      await socketResolveChat(selectedChatId);
      setSelectedChatId(null);
      setMessages([]);
      setActiveAgentChatId(null);
      setAgentParams({ tab: 'my' });
      await refreshAgentQueues();
      setTodayResolved((prev) => prev + 1);
      notifySuccess('Samtalen ble lukket.', { source: 'Agent' });
    } catch (err) {
      console.error('Failed to resolve chat', err);
      notifyError(err.message || 'Kunne ikke lukke samtalen.', { source: 'Agent' });
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'chat') {
      setAgentParams({ chatId: selectedChatId });
    } else {
      setAgentParams({ tab });
    }
  };

  if (!permissions?.canViewAgent) {
    return <Navigate to="/" replace />;
  }

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Agent</h1>
          <p>Velg et selskap for å se live agent-panelet.</p>
        </div>
      </div>
    );
  }

  const connectionLabel = isConnected
    ? 'Live-tilkobling aktiv'
    : connectionState === 'connecting'
      ? 'Kobler til live'
      : 'Live-tilkobling frakoblet';
  const availabilityLabel = agentDesiredAvailable && agentIsAvailable ? 'Tilkoblet' : 'Frakoblet';

  return (
    <div className="agent-page">
      {incomingOffer && (
        <div className="agent-offer-overlay">
          <div className="agent-offer-modal">
            <div className="agent-offer-header">
              <h3>Ny samtale venter</h3>
              <div className="agent-offer-timer">
                <div
                  className="agent-offer-timer-bar"
                  style={{ width: `${(offerTimeLeft / 30) * 100}%` }}
                />
                <span>{offerTimeLeft}s</span>
              </div>
            </div>
            <div className="agent-offer-content">
              {incomingOffer.escalation?.summary && (
                <p className="agent-offer-summary">{incomingOffer.escalation.summary}</p>
              )}
              {incomingOffer.escalation?.reason && (
                <p className="agent-offer-reason">
                  <strong>Grunn:</strong> {incomingOffer.escalation.reason}
                </p>
              )}
              {incomingOffer.preview?.lastMessage && (
                <p className="agent-offer-preview">
                  <strong>Siste melding:</strong> {incomingOffer.preview.lastMessage}
                </p>
              )}
              <div className="agent-offer-meta">
                <span className={`priority-badge priority-${incomingOffer.escalation?.priority || 'normal'}`}>
                  {incomingOffer.escalation?.priority === 'urgent'
                    ? 'Haster'
                    : incomingOffer.escalation?.priority === 'high'
                      ? 'Høy'
                      : 'Normal'}
                </span>
                {incomingOffer.preview?.messageCount && (
                  <span>{incomingOffer.preview.messageCount} meldinger</span>
                )}
              </div>
            </div>
            <div className="agent-offer-actions">
              <button className="btn btn-primary btn-lg" onClick={acceptOffer}>
                Godta samtale
              </button>
              <button className="btn btn-secondary" onClick={declineOffer}>
                Avslå
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="agent-page-head">
        <div>
          <p className="page-eyebrow">Live Support</p>
          <h1>Agent-panel</h1>
          <p className="page-subtitle">
            Håndter samtaler i sanntid. Status styres fra Agent-valget i menyen.
          </p>
        </div>
        <div className="agent-connection-meta" aria-live="polite">
          <span className={`agent-connection-pill ${agentDesiredAvailable && agentIsAvailable ? 'is-online' : ''}`}>
            {availabilityLabel}
          </span>
          <span className={`agent-connection-pill ${isConnected ? 'is-online' : 'is-offline'}`}>
            {connectionLabel}
          </span>
        </div>
      </div>

      <section className="agent-stats-strip" aria-label="Agentoversikt">
        <article className="agent-stat-chip">
          <span className="agent-stat-value">{pendingQueue.length}</span>
          <span className="agent-stat-label">i ventekø</span>
        </article>
        <article className="agent-stat-chip">
          <span className="agent-stat-value">{myChats.length}</span>
          <span className="agent-stat-label">mine samtaler</span>
        </article>
        <article className="agent-stat-chip">
          <span className="agent-stat-value">{todayResolved}</span>
          <span className="agent-stat-label">løst i dag</span>
        </article>
        <article className="agent-stat-chip">
          <span className="agent-stat-value">{agentQueuesLoading ? '...' : socketReady ? 'OK' : '-'}</span>
          <span className="agent-stat-label">live status</span>
        </article>
      </section>

      <nav className="agent-tabs" aria-label="Agentvisninger">
        {Object.entries(TAB_LABELS).map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            className={`agent-tab ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => switchTab(tab)}
          >
            <span>{label}</span>
            {tab === 'my' && <strong>{myChats.length}</strong>}
            {tab === 'queue' && <strong>{pendingQueue.length}</strong>}
          </button>
        ))}
      </nav>

      <main className="agent-panel">
        {activeTab === 'my' && (
          <section className="agent-list-view">
            <header className="agent-list-view-header">
              <h2>Mine samtaler</h2>
              <span>{myChats.length} aktive</span>
            </header>
            {myChats.length === 0 ? (
              <div className="agent-list-empty">Ingen aktive samtaler.</div>
            ) : (
              <div className="agent-list-grid">
                {myChats.map((chat) => (
                  <button
                    key={chat.chatId}
                    type="button"
                    className="agent-list-card"
                    onClick={() => selectChat(chat.chatId)}
                  >
                    <span className="agent-list-card-top">
                      <strong>{chat.originChannel || 'Web'}</strong>
                      <small>{formatRelativeTime(chat.lastMessageAt || chat.acceptedAt)}</small>
                    </span>
                    <span className="agent-list-card-preview">{getChatPreview(chat)}</span>
                    <span className="agent-list-card-meta">
                      {chat.botName || 'Bot'} - {chat.messageCount || 0} meldinger
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'queue' && (
          <section className="agent-list-view">
            <header className="agent-list-view-header">
              <h2>Ventekø</h2>
              <span>{pendingQueue.length} venter</span>
            </header>
            {pendingQueue.length === 0 ? (
              <div className="agent-list-empty">Ingen samtaler venter akkurat nå.</div>
            ) : (
              <div className="agent-list-grid">
                {pendingQueue.map((chat) => (
                  <article key={chat.chatId} className="agent-list-card agent-list-card--queue">
                    <span className="agent-list-card-top">
                      <strong>{chat.originChannel || 'Web'}</strong>
                      <small>{formatRelativeTime(chat.escalatedAt || chat.lastMessageAt)}</small>
                    </span>
                    <span className="agent-list-card-preview">{getChatPreview(chat)}</span>
                    <span className="agent-list-card-meta">
                      <span className={`priority-badge priority-${chat.priority || 'normal'}`}>
                        {chat.priority === 'urgent' ? 'Haster' : chat.priority === 'high' ? 'Høy' : 'Normal'}
                      </span>
                      {chat.botName || 'Bot'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => claimChat(chat.chatId)}
                    >
                      Ta samtale
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'chat' && (
          <section className="agent-chat-area">
            {selectedAssignedChat ? (
              <>
                <header className="agent-chat-header">
                  <div className="agent-chat-header-info">
                    <h3>Samtale</h3>
                    <span className="agent-chat-header-meta">
                      {selectedAssignedChat.botName && `Bot: ${selectedAssignedChat.botName}`}
                      {selectedAssignedChat.originChannel && ` - ${selectedAssignedChat.originChannel}`}
                      {selectedAssignedChat.messageCount && ` - ${selectedAssignedChat.messageCount} meldinger`}
                    </span>
                  </div>
                  <div className="agent-chat-header-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handBackToAI}
                      title="Send samtalen tilbake til AI-agenten"
                    >
                      Send til AI
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={resolveChat}
                      title="Merk samtalen som løst"
                    >
                      Løst
                    </button>
                  </div>
                </header>

                <div className="agent-chat-messages">
                  {messages.map((msg, idx) => {
                    const isCustomer = msg.role === 'user'
                      || msg.sender_type === 'customer'
                      || msg.sender_type === 'user';
                    const isAgent = msg.sender_type === 'agent';
                    const senderDisplayName = msg.sender_name
                      || (isCustomer ? 'Bruker' : isAgent ? 'Agent' : 'AI Agent');

                    return (
                      <div
                        key={msg.id || idx}
                        className={`agent-chat-message ${
                          isCustomer ? 'customer' : isAgent ? 'agent' : 'bot'
                        }`}
                      >
                        <div className="agent-chat-message-header">
                          <span className="agent-chat-message-sender">
                            {senderDisplayName}
                          </span>
                          <span className="agent-chat-message-time">
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                        <div className="agent-chat-message-content">
                          {msg.content || msg.message_content}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <div className="agent-chat-input">
                  <textarea
                    value={newMessage}
                    onChange={(event) => {
                      setNewMessage(event.target.value);
                      handleTyping();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Skriv en melding..."
                    rows={2}
                    disabled={isSending}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isSending}
                  >
                    {isSending ? '...' : 'Send'}
                  </button>
                </div>
              </>
            ) : selectedPendingChat ? (
              <div className="agent-chat-empty agent-chat-empty--accept">
                <div className="agent-chat-empty-icon" aria-hidden="true" />
                <h3>Samtalen venter i kø</h3>
                <p>
                  Denne samtalen er ikke tildelt deg ennå. Ta samtalen før du åpner meldingsfeltet.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => claimChat(selectedPendingChat.chatId)}
                >
                  Ta samtale
                </button>
              </div>
            ) : (
              <div className="agent-chat-empty">
                <div className="agent-chat-empty-icon" aria-hidden="true" />
                <h3>Velg en samtale</h3>
                <p>
                  Velg en aktiv samtale fra menyen eller fanen Mine samtaler.
                  {!agentDesiredAvailable && (
                    <span className="agent-chat-empty-hint">
                      <br /><br />
                      Slå på Tilkoblet i menyen for å motta nye samtaler.
                    </span>
                  )}
                </p>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default AgentPage;
